import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Download, Plus, Ticket, Repeat, Search } from 'lucide-react'
import { getContentList, exportListUrl } from '../../lib/api/content'
import { useToggleEnabled } from '../conventions/useToggleEnabled'
import { PageHead, Stat, Switch } from '../config/ui'
import { CouponEditDrawer } from './CouponEditDrawer'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0)

const LIST_KEY = ['content', 'coupon', 'all'] as const

/** discount text → "−20%" or "−€10,00" (numeric = EUR, with % = percentage). */
function formatDiscount(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return '—'
  if (s.includes('%')) {
    const m = s.match(/(\d+(?:[.,]\d+)?)/)
    return m ? `−${m[1].replace(',', '.')}%` : s
  }
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n)
    ? `−${new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n)}`
    : s
}

const isoToShort = (v: unknown): string => {
  const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : ''
}
function fmtRange(validity: unknown): string {
  const v = (validity ?? {}) as El
  const from = isoToShort(v.fromDay)
  const to = isoToShort(v.toDay)
  if (!from && !to) return 'Sempre'
  return `${from || '…'} → ${to || '…'}`
}

const SEG = [
  { value: 'all', label: 'Tutti' },
  { value: 'active', label: 'Attivi' },
  { value: 'inactive', label: 'Disattivi' },
] as const

type Row = {
  id: string
  name: string
  code: string
  discount: string
  range: string
  uses: number
  reusable: boolean
  enabled: boolean
}

export function CouponsPage() {
  const [seg, setSeg] = useState<(typeof SEG)[number]['value']>('all')
  const [q, setQ] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [editId, setEditId] = useState<string | null | undefined>(undefined) // undefined=closed, null=new

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => getContentList('coupon', { limit: 500 }),
  })
  const toggle = useToggleEnabled('coupon', LIST_KEY)

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? [])
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? ''),
          code: String(el.code ?? ''),
          discount: String(el.discount ?? ''),
          range: fmtRange(el.validity),
          uses: num(el.useCount ?? el.reuseNumber),
          reusable: isYes(el.reusable),
          enabled: isYes(el.enabled),
        }
      })
      .sort((a, b) => a.code.localeCompare(b.code, 'it'))
  }, [list.data])

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.enabled).length
    const uses = rows.reduce((s, r) => s + r.uses, 0)
    return { total: rows.length, active, uses }
  }, [rows])

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase()
    return rows.filter((r) => {
      if (seg === 'active' && !r.enabled) return false
      if (seg === 'inactive' && r.enabled) return false
      if (n && !r.code.toLowerCase().includes(n) && !r.name.toLowerCase().includes(n)) return false
      return true
    })
  }, [rows, seg, q])

  function flip(r: Row) {
    setBusyId(r.id)
    toggle.mutate({ id: r.id, enabled: !r.enabled }, { onSettled: () => setBusyId(null) })
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Coupon"
        description="Codici sconto per campagne, partner e recupero clienti — con validità, riutilizzo e tipi di veicolo ammessi."
      >
        <a
          href={exportListUrl('coupon', { sort: 'code asc' })}
          className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
        >
          <Download className="h-4 w-4" /> Esporta Excel
        </a>
        <button
          onClick={() => setEditId(null)}
          className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white hover:bg-verdigris-700"
        >
          <Plus className="h-4 w-4" /> Nuovo coupon
        </button>
      </PageHead>

      <div className="mb-[18px] grid grid-cols-3 gap-[14px]">
        <Stat label="Coupon" value={String(stats.total)} sub="codici totali" />
        <Stat label="Attivi" value={String(stats.active)} sub="utilizzabili ora" />
        <Stat label="Utilizzi" value={stats.uses.toLocaleString('it-IT')} sub="riscatti totali" />
      </div>

      {/* toolbar */}
      <div className="mb-[18px] flex flex-wrap items-center gap-2.5">
        <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
          {SEG.map((s) => (
            <button
              key={s.value}
              onClick={() => setSeg(s.value)}
              className={cn(
                'px-3.5 py-2 text-[13px] font-semibold transition-colors',
                seg === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca codice o nome…"
            className="h-10 w-full rounded-lg border border-line bg-card pl-9 pr-3 text-[13.5px] text-coal outline-none placeholder:text-faint focus:border-verdigris"
          />
        </div>
      </div>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i coupon.
        </div>
      ) : (
        <div className="cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                  <th className="px-5 py-3 text-left">Codice</th>
                  <th className="px-4 py-3 text-left">Nome</th>
                  <th className="px-4 py-3 text-right">Sconto</th>
                  <th className="px-4 py-3 text-left">Validità</th>
                  <th className="px-4 py-3 text-right">Utilizzi</th>
                  <th className="px-4 py-3 text-center">Riutilizz.</th>
                  <th className="px-4 py-3 text-right">Attivo</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft last:border-0">
                      {Array.from({ length: 7 }).map((_c, j) => (
                        <td key={j} className="px-5 py-3.5">
                          <div className="skeleton h-3.5 w-20 rounded-sm bg-line-soft" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-sm text-muted">
                      {rows.length === 0 ? 'Nessun coupon. Crea il primo con “Nuovo coupon”.' : 'Nessun coupon nel filtro.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setEditId(r.id)}
                      className={cn(
                        'cursor-pointer border-b border-line-soft last:border-0 hover:bg-paper',
                        !r.enabled && 'opacity-60',
                      )}
                    >
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-oro/40 bg-oro-soft px-2 py-1 font-mono text-[12.5px] font-bold tracking-wide text-oro-700">
                          <Ticket className="h-3.5 w-3.5" /> {r.code || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-coal">{r.name || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-bold text-verdigris">{formatDiscount(r.discount)}</span>
                      </td>
                      <td className="px-4 py-3 text-slate">{r.range}</td>
                      <td className="px-4 py-3 text-right font-mono tnum text-slate">{r.uses.toLocaleString('it-IT')}</td>
                      <td className="px-4 py-3 text-center">
                        {r.reusable ? (
                          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-ok" title="Riutilizzabile">
                            <Repeat className="h-3.5 w-3.5" /> Sì
                          </span>
                        ) : (
                          <span className="text-[12px] text-muted">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">
                          <Switch on={r.enabled} busy={busyId === r.id} onClick={() => flip(r)} label={`Attiva ${r.code}`} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editId !== undefined && <CouponEditDrawer couponId={editId} onClose={() => setEditId(undefined)} listKey={LIST_KEY} />}
    </div>
  )
}
